import React, { useCallback, useEffect, useState } from "react";
import API_BASE from "../config/api";
import { resolveImageUrl } from "../utils/siteHelpers";

/*
  Owner inbox for the three Contact forms on the public site:
  messages, appointment requests, donations. Reads/writes only rows for the
  caller's own domain (checked server-side by personaluid).
*/

const APPT_STATUSES = ["pending", "approved", "rejected", "completed"];
const DON_STATUSES = ["pending", "verified", "rejected"];
const BADGE = { pending: "warning text-dark", approved: "success", verified: "success", rejected: "danger", completed: "secondary" };

function fmt(dt) {
    if (!dt) return "";
    const d = new Date(String(dt).replace(" ", "T"));
    return isNaN(d) ? dt : d.toLocaleString();
}

export default function PoliticalInboxTab({ personaluid }) {
    const [sub, setSub] = useState("messages");
    const [data, setData] = useState({ messages: [], appointments: [], donations: [] });
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [proof, setProof] = useState("");

    const load = useCallback(() => {
        if (!personaluid) { setLoading(false); return; }
        setLoading(true);
        setErr("");
        fetch(`${API_BASE}/api/get_political_inbox.php?personaluid=${encodeURIComponent(personaluid)}`)
            .then((r) => r.json())
            .then((res) => {
                if (res.success) setData({ messages: res.messages || [], appointments: res.appointments || [], donations: res.donations || [] });
                else setErr(res.message || "Could not load inbox.");
            })
            .catch(() => setErr("Network error while loading."))
            .finally(() => setLoading(false));
    }, [personaluid]);

    useEffect(() => { load(); }, [load]);

    const act = async (kind, id, action, status) => {
        if (action === "delete" && !window.confirm("Delete this entry permanently?")) return;
        try {
            const res = await fetch(`${API_BASE}/api/update_political_inbox.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ personaluid, kind, id, action, status }),
            });
            const out = await res.json();
            if (out.success) load(); else setErr(out.message || "Action failed.");
        } catch {
            setErr("Network error.");
        }
    };

    const unread = data.messages.filter((m) => !Number(m.is_read)).length;
    const pendingAppt = data.appointments.filter((a) => a.status === "pending").length;
    const pendingDon = data.donations.filter((d) => d.status === "pending").length;
    const verifiedTotal = data.donations.filter((d) => d.status === "verified").reduce((s, d) => s + Number(d.amount || 0), 0);

    const pill = (key, label, n) => (
        <li className="nav-item" key={key}>
            <button type="button" className={`nav-link ${sub === key ? "active" : ""}`} onClick={() => setSub(key)}>
                {label} {n > 0 && <span className="badge bg-danger ms-1">{n}</span>}
            </button>
        </li>
    );

    return (
        <div className="card shadow-sm mb-3">
            <div className="card-header bg-light fw-semibold d-flex justify-content-between align-items-center">
                <span>Inbox</span>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={load} disabled={loading}>
                    <i className="bi bi-arrow-clockwise me-1" />{loading ? "Loading..." : "Refresh"}
                </button>
            </div>
            <div className="card-body">
                {err && <div className="alert alert-danger py-2">{err}</div>}
                <ul className="nav nav-pills mb-3">
                    {pill("messages", "Messages", unread)}
                    {pill("appointments", "Appointments", pendingAppt)}
                    {pill("donations", "Donations", pendingDon)}
                </ul>

                {sub === "messages" && (data.messages.length === 0 ? <p className="text-muted mb-0">No messages yet.</p> : data.messages.map((m) => (
                    <div key={m.id} className={`border rounded p-3 mb-2 ${Number(m.is_read) ? "" : "border-primary"}`}>
                        <div className="d-flex justify-content-between flex-wrap gap-2">
                            <div><b>{m.subject}</b> {!Number(m.is_read) && <span className="badge bg-primary ms-1">New</span>}</div>
                            <small className="text-muted">{fmt(m.created_at)}</small>
                        </div>
                        <div className="small text-muted mb-2">{m.name} · <a href={`mailto:${m.email}`}>{m.email}</a></div>
                        <div style={{ whiteSpace: "pre-wrap" }}>{m.message}</div>
                        <div className="mt-2 d-flex gap-2">
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => act("message", m.id, Number(m.is_read) ? "unread" : "read")}>
                                Mark {Number(m.is_read) ? "unread" : "read"}
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => act("message", m.id, "delete")}><i className="bi bi-trash" /></button>
                        </div>
                    </div>
                )))}

                {sub === "appointments" && (data.appointments.length === 0 ? <p className="text-muted mb-0">No appointment requests yet.</p> : data.appointments.map((a) => (
                    <div key={a.id} className="border rounded p-3 mb-2">
                        <div className="d-flex justify-content-between flex-wrap gap-2">
                            <div><b>{a.full_name}</b> <span className={`badge bg-${BADGE[a.status]} ms-1`}>{a.status}</span></div>
                            <small className="text-muted">{fmt(a.created_at)}</small>
                        </div>
                        <div className="small mb-1">
                            <i className="bi bi-telephone me-1" /><a href={`tel:${a.mobile}`}>{a.mobile}</a>
                            {a.address && <> · <i className="bi bi-geo-alt me-1" />{a.address}</>}
                        </div>
                        <div className="small"><b>Purpose:</b> {a.purpose}{a.preferred_date && <> · <b>Preferred date:</b> {a.preferred_date}</>}</div>
                        {a.message && <div className="mt-1" style={{ whiteSpace: "pre-wrap" }}>{a.message}</div>}
                        <div className="mt-2 d-flex gap-2 align-items-center flex-wrap">
                            <select className="form-select form-select-sm" style={{ width: 150 }} value={a.status} onChange={(e) => act("appointment", a.id, "status", e.target.value)}>
                                {APPT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => act("appointment", a.id, "delete")}><i className="bi bi-trash" /></button>
                        </div>
                    </div>
                )))}

                {sub === "donations" && (
                    <>
                        <div className="alert alert-light border py-2 small">
                            Verified so far: <b>NPR {verifiedTotal.toLocaleString()}</b> · Pending: <b>{pendingDon}</b>
                        </div>
                        {data.donations.length === 0 ? <p className="text-muted mb-0">No donations yet.</p> : data.donations.map((d) => (
                            <div key={d.id} className="border rounded p-3 mb-2">
                                <div className="d-flex justify-content-between flex-wrap gap-2">
                                    <div>
                                        <b>NPR {Number(d.amount).toLocaleString()}</b>
                                        <span className={`badge bg-${BADGE[d.status]} ms-2`}>{d.status}</span>
                                        {d.method && <span className="badge bg-info text-dark ms-1">{d.method}</span>}
                                    </div>
                                    <small className="text-muted">{fmt(d.created_at)}</small>
                                </div>
                                <div className="small text-muted">
                                    {d.donor_name || "Anonymous"}{d.donor_phone && <> · {d.donor_phone}</>} · <code>{d.transaction_id}</code>
                                </div>
                                <div className="mt-2 d-flex gap-2 align-items-center flex-wrap">
                                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setProof(resolveImageUrl(API_BASE, d.screenshot))}>
                                        <i className="bi bi-image me-1" />View proof
                                    </button>
                                    <select className="form-select form-select-sm" style={{ width: 140 }} value={d.status} onChange={(e) => act("donation", d.id, "status", e.target.value)}>
                                        {DON_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => act("donation", d.id, "delete")}><i className="bi bi-trash" /></button>
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>

            {proof && (
                <div onClick={() => setProof("")} role="dialog" aria-modal="true"
                    style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,.8)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
                    <img src={proof} alt="Payment proof" style={{ maxWidth: "100%", maxHeight: "90vh", borderRadius: 8, background: "#fff" }} />
                </div>
            )}
        </div>
    );
}